'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('light');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  // Get system preference
  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  // Apply theme to document
  const applyTheme = useCallback(
    (newTheme: Theme) => {
      const root = document.documentElement;
      const resolved = newTheme === 'system' ? getSystemTheme() : newTheme;

      if (resolved === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      setResolvedTheme(resolved);
    },
    [getSystemTheme]
  );

  // Set theme and persist to localStorage
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('sentrial-theme', newTheme);
    applyTheme(newTheme);
  };

  // Initialize theme on mount - default to light if nothing stored
  useEffect(() => {
    // localStorage is external, user-writable storage — validate against the Theme union instead of
    // asserting, so a legacy value from the retired theme scheme (or a tampered value) can't escape
    // into state typed 'light' | 'dark' | 'system' and poison every resolvedTheme comparison.
    const stored = localStorage.getItem('sentrial-theme');
    const initialTheme: Theme =
      stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'light';
    setThemeState(initialTheme);
    applyTheme(initialTheme);
  }, [applyTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme('system');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  // No mount gate: the pre-hydration inline script in app/layout.tsx already sets the `dark` class
  // before first paint, so ThemeProvider can render children immediately (SSR emits real content)
  // without a theme flash. State still initializes in the mount effect above.
  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
